import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, TrendingUp, TrendingDown, ArrowUpRight, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import YieldMethodologyInfo from "@/components/common/YieldMethodologyInfo";

// Locale-aware integer formatter, mirrors MarketWidgets/Market2026Dialog.
const fmt = (n, isAR = false) => {
  const locale = isAR ? "ar-EG" : "en-US";
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n || 0).replace(/\u066C/g, ",");
};

// Convert DLD spark_months ("01 '26") → readable label ("Jan '26").
const MONTH_ABBR = { "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr", "05": "May", "06": "Jun",
                     "07": "Jul", "08": "Aug", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec" };
const MONTH_ABBR_AR = { "01": "ينا", "02": "فبر", "03": "مار", "04": "أبر", "05": "ماي", "06": "يون",
                        "07": "يول", "08": "أغس", "09": "سبت", "10": "أكت", "11": "نوف", "12": "ديس" };
function humanMonth(label, lang) {
  const m = /^(\d{2}) '(\d{2})$/.exec(label || "");
  if (!m) return label || "";
  const table = lang === "ar" ? MONTH_ABBR_AR : MONTH_ABBR;
  return `${table[m[1]] || m[1]} '${m[2]}`;
}

export default function MarketAreaDrawer({ area, onClose }) {
  const { lang, isRTL } = useLang();
  const { format } = useCurrency();
  const open = Boolean(area);

  useEffect(() => {
    // Window capture runs before the underlying sheet's document listener, so one Esc closes only this drawer.
    const onEsc = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    if (open) window.addEventListener("keydown", onEsc, true);
    return () => window.removeEventListener("keydown", onEsc, true);
  }, [open, onClose]);

  // Accessible dialog: focus inside on open, restore to the opener on close.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const t = setTimeout(() => document.querySelector("[data-testid=area-drawer] button")?.focus(), 50);
    return () => { clearTimeout(t); opener?.focus?.(); };
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const chartData = useMemo(() => {
    // Live DLD monthly medians attached by cron.sync_market_areas as `spark`
    // + parallel `spark_months` labels (e.g. "04 '26"). Falls back to the
    // static 6-value spark if the DLD sync hasn't populated the area yet.
    const values = (area?.spark || []).map((v) => (typeof v === "object" ? v.v : v));
    const rawLabels = area?.spark_months || [];
    return values.map((v, i) => ({
      month: humanMonth(rawLabels[i], lang),
      value: v,
    }));
  }, [area, lang]);

  const rawSpark = Array.isArray(area?.spark) ? area.spark.map((v) => (typeof v === "object" ? v.v : v)) : [];
  const currentPrice = rawSpark[rawSpark.length - 1];
  const firstPrice = rawSpark[0];
  const priceDelta = currentPrice && firstPrice ? ((currentPrice - firstPrice) / firstPrice) * 100 : 0;
  const asOfLabel = area?.as_of || "";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="area-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          style={{ zIndex: 90 }}
          className="fixed inset-0 bg-propertic-navy/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          data-testid="area-drawer-backdrop"
        >
          <motion.div
            key="area-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={area?.area || "Area report"}
            className="relative bg-propertic-paper-2 rounded-[1.5rem] sm:rounded-[2rem] w-full max-w-2xl max-h-[92dvh] flex flex-col overflow-hidden border hairline shadow-2xl"
            data-testid="area-drawer"
          >
            <div className="sticky top-0 z-10 bg-propertic-paper-2/95 backdrop-blur-md border-b hairline">
              <div className="flex items-start justify-between p-5 sm:p-6">
                <div className="min-w-0 pr-3">
                  <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-1.5">{lang === "ar" ? "دبي · دائرة الأراضي · تحديث أسبوعي" : "Dubai · DLD · Weekly refresh"}</p>
                  <h3 className="font-editorial text-lg sm:text-xl leading-[1.15] text-propertic-ink truncate">
                    {lang === "ar" ? area.area_ar : area.area}
                  </h3>
                  <p className="mt-1.5 text-[13px] text-propertic-ink-2/95 max-w-md leading-relaxed line-clamp-2">
                    {lang === "ar" ? (area.positioning_ar || area.positioning) : area.positioning}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  data-testid="area-drawer-close"
                  aria-label={lang === "ar" ? "إغلاق" : "Close"}
                  className="shrink-0 w-9 h-9 rounded-full border hairline grid place-items-center text-propertic-navy bg-white/90 hover:bg-propertic-navy hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 sm:p-6 space-y-5 overflow-y-auto flex-1 overscroll-contain">
              {/* Headline stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                <StatBig
                  lang={lang}
                  label={lang === "ar" ? "مؤشّر وسيط للقدم²" : "Median-based / sqft"}
                  value={`${area.verified ? "" : "≈ "}${format(area.avg_price_sqft_aed, { decimals: 0 })}`}
                  delta={area.yoy_change_pct}
                />
                <StatBig
                  lang={lang}
                  label={lang === "ar" ? "العائد الإجمالي" : "Gross yield"}
                  value={`${area.verified ? "" : "≈ "}${area.rental_yield_pct}%`}
                  tone="gold"
                  info={<YieldMethodologyInfo />}
                />
                {area.villa_price_sqft_aed ? (
                  <StatBig
                    lang={lang}
                    label={lang === "ar" ? "فيلا للقدم²" : "Villa / sqft"}
                    value={`${area.verified ? "" : "≈ "}${format(area.villa_price_sqft_aed, { decimals: 0 })}`}
                  />
                ) : null}
                <StatBig
                  lang={lang}
                  label={lang === "ar" ? "مبيعات مسجّلة ٢٠٢٦ حتى اليوم" : "YTD '26 sales"}
                  value={`${area.verified ? "" : "≈ "}${fmt(area.transactions_last_quarter, lang === "ar")}`}
                />
              </div>

              {/* Filter chips */}
              <div>
                <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-1">{lang === "ar" ? "اتجاه السعر" : "Price trend"}</p>
                    <p className="font-editorial text-sm sm:text-base text-propertic-navy leading-tight">
                      {lang === "ar" ? "الوسيط الشهري لسعر القدم²" : "Monthly median price / sqft"}
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300/40" data-testid="area-drawer-live-badge">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
                      <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    </span>
                    <span className="font-mono-brand text-emerald-700 text-[9px] tracking-[0.14em] uppercase">
                      {lang === "ar" ? "دائرة الأراضي · تحديث أسبوعي" : "DLD · Weekly"}
                    </span>
                  </div>
                </div>

                <div className="h-44 sm:h-48 bg-white rounded-xl border hairline p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#B08543" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#B08543" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#E4DDCC" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" stroke="#6E6A62" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                      <YAxis stroke="#6E6A62" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} width={48} />
                      <Tooltip
                        cursor={{ stroke: "#B08543", strokeWidth: 1, strokeDasharray: "3 3" }}
                        contentStyle={{ background: "#FBF9F4", border: "1px solid #E4DDCC", fontSize: 11, borderRadius: 10 }}
                        labelStyle={{ color: "#B08543", fontFamily: "JetBrains Mono", fontSize: 10 }}
                        formatter={(v) => (lang === "ar" ? [`${fmt(v, true)} درهم`, "السعر لكل قدم مربّعة"] : [`AED ${fmt(v)}`, "Price/sqft"])}
                      />
                      <Area type="monotone" dataKey="value" stroke="#B08543" strokeWidth={2} fill="url(#areaGradient)" dot={{ fill: "#0A2540", r: 2 }} activeDot={{ r: 4, fill: "#B08543" }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <p className="mt-2 text-[10px] text-propertic-muted leading-relaxed">
                  {lang === "ar"
                    ? "الأسعار مستمدة مباشرة من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك في دبي، وتُحدَّث كل اثنين ١١:٠٠ بتوقيت الإمارات المحلي."
                    : "Sourced live from the Dubai Land Department Open Data Gateway, refreshed every Monday 11:00 UAE local time."}
                  {` `}
                  <span className="tabular force-ltr text-propertic-navy font-medium">
                    {priceDelta >= 0 ? "+" : ""}{priceDelta.toFixed(1)}%
                  </span>{" "}
                  {lang === "ar" ? "خلال الفترة المعروضة" : "over the period shown"}.
                  {asOfLabel ? (
                    <span className="block mt-0.5 opacity-70">{asOfLabel}</span>
                  ) : null}
                </p>
              </div>

              {/* Rent table */}
              <div>
                <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-1">{lang === "ar" ? "الإيجارات السنوية" : "Annual rents"}</p>
                <p className="font-editorial text-sm sm:text-base text-propertic-navy leading-tight mb-3">
                  {lang === "ar" ? "متوسط الإيجار حسب النوع" : "Average rent by unit type"}
                </p>                <div className="bg-white rounded-xl border hairline overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody className="divide-y hairline">
                      {[
                        { k: "studio", en: "Studio", ar: "استوديو", v: area.avg_rent_studio_aed },
                        { k: "1br", en: "1 Bedroom", ar: "غرفة واحدة", v: area.avg_rent_1br_aed },
                        { k: "2br", en: "2 Bedroom", ar: "غرفتان", v: area.avg_rent_2br_aed },
                        { k: "3br", en: "3 Bedroom", ar: "٣ غرف", v: area.avg_rent_3br_aed },
                      ].map((r) => (
                        <tr key={r.k} className="hover:bg-propertic-sand/30 transition-colors">
                          <td className="p-3 font-editorial text-[13px] text-propertic-navy">{lang === "ar" ? r.ar : r.en}</td>
                          <td className="p-3 text-end tabular text-propertic-ink force-ltr font-medium text-[13px]">
                            {area.verified ? "" : "≈ "}{format(r.v, { decimals: 0 })}<span className="text-propertic-muted text-[10px] font-sans ms-1">{lang === "ar" ? "سنوياً" : "/yr"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-[10px] text-propertic-muted leading-relaxed" data-testid="drawer-rent-source">
                  {lang === "ar"
                    ? "المرجع: متوسط أسعار الطلب في إعلانات بوابات العقارات الإلكترونية (لقطة النصف الأول 2026). ليست أسعار عقود إيجاري."
                    : "Reference: average asking rents from online property portal listings (H1 2026 snapshot). Not Ejari contract rents."}
                </p>
              </div>

              {/* Notable projects */}
              {area.projects?.length > 0 && (
                <div>
                  <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-2">{lang === "ar" ? "مشاريع بارزة" : "Notable projects"}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {area.projects.map((p) => (
                      <span key={p} className="px-2.5 py-1 rounded-full bg-propertic-sand border hairline text-[11px] text-propertic-ink-2">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <a
                href="#consultation"
                onClick={onClose}
                data-testid="area-drawer-cta"
                className="bg-propertic-navy hover:bg-propertic-navy-2 text-propertic-paper inline-flex items-center gap-2 h-12 px-6 rounded-full font-mono-brand text-[11px] tracking-[0.22em] uppercase w-full justify-center transition-colors"
              >
                {lang === "ar" ? (
                  <>
                    <span className="hidden sm:inline">احجز استشارتك المجانية للاستثمار العقاري</span>
                    <span className="sm:hidden">احجز استشارة مجانية</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Book your free real estate investment consultation</span>
                    <span className="sm:hidden">Book Free Consultation</span>
                  </>
                )}
                <ArrowUpRight className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""}`} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function StatBig({ label, value, delta, tone, lang, info }) {
  const isPos = delta == null ? null : delta >= 0;
  const isAR = lang === "ar";
  return (
    <div className={`rounded-xl p-3 border hairline ${tone === "gold" ? "bg-propertic-navy text-propertic-paper border-propertic-navy" : "bg-white"}`}>
      <p className={`font-mono-brand text-[9px] tracking-[0.18em] uppercase inline-flex items-center gap-1 ${tone === "gold" ? "text-propertic-gold-2" : "text-propertic-muted"}`}>{label}{info}</p>
      <p className={`font-editorial text-lg sm:text-xl mt-1 tabular force-ltr leading-none ${tone === "gold" ? "text-propertic-paper" : "text-propertic-navy"}`}>{value}</p>
      {delta != null && (
        <p className={`mt-1.5 text-[10px] font-mono-brand tabular force-ltr inline-flex items-center gap-1 ${isPos ? "text-emerald-600" : "text-red-600"}`}>
          {isPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {isPos ? "+" : ""}{delta}% {isAR ? "سنوياً" : "YoY"}
        </p>
      )}
    </div>
  );
}
